import { devLog } from './devLog';

// 智能樂器ID類型映射系統
type InstrumentType = 'kick' | 'snare' | 'hihat' | 'openhat' | 'tom' | 'clap' | 'crash' | 'ride' | 'percussion';

interface InstrumentConfig {
  name: string;
  type: InstrumentType;
  sampleId: string;
  color: string;
}

interface TypePattern {
  patterns: RegExp;
  type: InstrumentType;
  sampleId: string;
  color: string;
  getName: (id: string) => string;
}

interface AnalysisResult extends InstrumentConfig {
  id: string;
  matchType: 'exact' | 'pattern' | 'fallback';
}

const typeMapping: Record<InstrumentType, string[]> = {
  kick: ['BD', 'KD'],
  snare: ['SD', 'SN'], 
  hihat: ['CH', 'HH'],
  openhat: ['OH', 'OC'],
  tom: ['T1', 'T2', 'T3', 'HT', 'MT', 'LT'],
  clap: ['CP', 'CL'],
  crash: ['CR', 'CY'],
  ride: ['RD', 'RC'],
  percussion: ['PC', 'PX', 'PY', 'PZ', 'CB', 'SH', 'RS']
};

// 精確ID配置映射
const exactConfigs: Record<string, InstrumentConfig> = {
  'BD': { name: 'Kick', type: 'kick', sampleId: 'kick-classic', color: 'bg-green-600' },
  'KD': { name: 'Kick Drum', type: 'kick', sampleId: 'kick-classic', color: 'bg-green-600' },
  'SD': { name: 'Snare', type: 'snare', sampleId: 'snare-analog', color: 'bg-red-600' },
  'SN': { name: 'Snare Drum', type: 'snare', sampleId: 'snare-analog', color: 'bg-red-600' },
  'CH': { name: 'Closed Hat', type: 'hihat', sampleId: 'hihat-acoustic01', color: 'bg-yellow-600' },
  'HH': { name: 'Hi-Hat', type: 'hihat', sampleId: 'hihat-acoustic01', color: 'bg-yellow-600' },
  'OH': { name: 'Open Hat', type: 'openhat', sampleId: 'openhat-acoustic01', color: 'bg-yellow-300' },
  'OC': { name: 'Open Hat', type: 'openhat', sampleId: 'openhat-acoustic01', color: 'bg-yellow-300' },
  'CP': { name: 'Clap', type: 'clap', sampleId: 'clap-analog', color: 'bg-purple-400' },
  'CL': { name: 'Clap', type: 'clap', sampleId: 'clap-analog', color: 'bg-purple-400' },
  'CB': { name: 'Cowbell', type: 'percussion', sampleId: 'cowbell', color: 'bg-orange-500' },
  'SH': { name: 'Shaker', type: 'percussion', sampleId: 'shaker', color: 'bg-pink-400' },
  'PC': { name: 'Perc', type: 'percussion', sampleId: 'perc-analog', color: 'bg-gray-500' },
  'CR': { name: 'Crash', type: 'crash', sampleId: 'crash-analog', color: 'bg-red-400' },
  'CY': { name: 'Crash', type: 'crash', sampleId: 'crash-analog', color: 'bg-red-400' },
  'RD': { name: 'Ride', type: 'ride', sampleId: 'crash-analog', color: 'bg-orange-400' },
  'RC': { name: 'Ride', type: 'ride', sampleId: 'crash-analog', color: 'bg-orange-400' },
  'RS': { name: 'Rimshot', type: 'hihat', sampleId: 'hihat-ring', color: 'bg-pink-500' },
  'T1': { name: 'High Tom', type: 'tom', sampleId: 'tom-acoustic01', color: 'bg-blue-400' },
  'T2': { name: 'Mid Tom', type: 'tom', sampleId: 'tom-acoustic02', color: 'bg-blue-500' },
  'T3': { name: 'Low Tom', type: 'tom', sampleId: 'tom-analog', color: 'bg-blue-700' },
  'HT': { name: 'High Tom', type: 'tom', sampleId: 'tom-acoustic01', color: 'bg-blue-400' },
  'MT': { name: 'Mid Tom', type: 'tom', sampleId: 'tom-acoustic02', color: 'bg-blue-500' },
  'LT': { name: 'Low Tom', type: 'tom', sampleId: 'tom-analog', color: 'bg-blue-700' }
};

// 模式匹配配置
const typePatterns: TypePattern[] = [
  // Kick patterns: BD, KD, BD1, BD2, KD1, etc.
  {
    patterns: /^(BD|KD)\d*$/i,
    type: 'kick',
    sampleId: 'kick-classic',
    color: 'bg-green-600',
    getName: (id: string) => id.toLowerCase().startsWith('bd') ? 'Bass Drum' : 'Kick Drum'
  },
  // Snare patterns: SD, SN, SD1, SN1, etc.
  {
    patterns: /^(SD|SN)\d*$/i,
    type: 'snare',
    sampleId: 'snare-analog',
    color: 'bg-red-600',
    getName: (id: string) => 'Snare Drum'
  },
  // Hi-hat patterns: CH, HH, CH1, HH1, etc.
  {
    patterns: /^(CH|HH)\d*$/i,
    type: 'hihat',
    sampleId: 'hihat-acoustic01',
    color: 'bg-yellow-600',
    getName: (id: string) => id.toLowerCase().startsWith('ch') ? 'Closed Hat' : 'Hi-Hat'
  },
  // Open hat patterns: OH, OC, OH1, OC1, etc.
  {
    patterns: /^(OH|OC)\d*$/i,
    type: 'openhat',
    sampleId: 'openhat-acoustic01',
    color: 'bg-yellow-300',
    getName: (id: string) => 'Open Hat'
  },
  // Tom patterns: T1, T2, T3, HT, MT, LT, HT1, MT1, LT1, etc.
  {
    patterns: /^(T\d+|HT|MT|LT)\d*$/i,
    type: 'tom',
    sampleId: 'tom-acoustic01',
    color: 'bg-blue-400',
    getName: (id: string) => {
      if (id.toLowerCase().includes('ht') || id === 'T1') return 'High Tom';
      if (id.toLowerCase().includes('mt') || id === 'T2') return 'Mid Tom';
      if (id.toLowerCase().includes('lt') || id === 'T3') return 'Low Tom';
      return 'Tom';
    }
  },
  // Clap patterns: CP, CL, CP1, CL1, etc.
  {
    patterns: /^(CP|CL)\d*$/i,
    type: 'clap',
    sampleId: 'clap-analog',
    color: 'bg-purple-400',
    getName: (id: string) => 'Clap'
  },
  // Crash patterns: CR, CY, CR1, CY1, etc.
  {
    patterns: /^(CR|CY)\d*$/i,
    type: 'crash',
    sampleId: 'crash-analog',
    color: 'bg-red-400',
    getName: (id: string) => 'Crash'
  },
  // Ride patterns: RD, RC, RD1, RC1, etc.
  {
    patterns: /^(RD|RC)\d*$/i,
    type: 'ride',
    sampleId: 'crash-analog', // 使用 crash 作為 ride 的替代
    color: 'bg-orange-400',
    getName: (id: string) => 'Ride'
  },
  // Percussion patterns: PC, PX, PY, PZ, PC1, etc.
  {
    patterns: /^(PC|PX|PY|PZ)\d*$/i,
    type: 'percussion',
    sampleId: 'perc-analog',
    color: 'bg-gray-500',
    getName: (id: string) => 'Percussion'
  }
];

/**
 * 智能反向映射：根據樂器ID推測樂器配置
 * @param id - 樂器ID
 * @param debugMode - 是否啟用debug模式
 * @returns 樂器配置物件
 */
export const getInstrumentConfigById = (id: string, debugMode: boolean = false): InstrumentConfig => {
  // 首先嘗試精確匹配
  if (exactConfigs[id]) {
    if (debugMode) {
      devLog('🎯 [Smart ID] Exact match found:', id, '→', exactConfigs[id].type);
    }
    return exactConfigs[id];
  }
  
  // 智能模式匹配：基於類型映射反向推理
  for (const pattern of typePatterns) {
    if (pattern.patterns.test(id)) {
      const config: InstrumentConfig = {
        name: pattern.getName(id),
        type: pattern.type,
        sampleId: pattern.sampleId,
        color: pattern.color
      };
      if (debugMode) {
        devLog('🔍 [Smart ID] Pattern match found:', id, '→', config.type, 'via pattern:', pattern.patterns);
      }
      return config;
    }
  }
  
  // 如果都找不到，回退到percussion
  if (debugMode) {
    devLog('❓ [Smart ID] No pattern match for ID:', id, '→ defaulting to percussion');
  }
  return { 
    name: id, 
    type: 'percussion', 
    sampleId: 'perc-analog', 
    color: 'bg-gray-400' 
  };
};

/**
 * 智能ID生成：為樂器類型生成適當的ID
 * @param type - 樂器類型
 * @param existingIds - 現有ID列表
 * @param debugMode - 是否啟用debug模式
 * @returns 生成的ID
 */
export const getSmartInstrumentId = (type: InstrumentType, existingIds: string[] = [], debugMode: boolean = false): string => {
  const typeIds = typeMapping[type] || ['PC'];
  
  // 嘗試使用類型的基本ID
  for (const baseId of typeIds) {
    if (!existingIds.includes(baseId)) {
      if (debugMode) {
        devLog('🎯 [Smart ID] Generated base ID:', baseId, 'for type:', type);
      }
      return baseId;
    }
  }
  
  // 如果基本ID已存在，則生成帶數字的ID
  for (const baseId of typeIds) {
    for (let i = 1; i <= 99; i++) {
      const numberedId = `${baseId}${i}`;
      if (!existingIds.includes(numberedId)) {
        if (debugMode) {
          devLog('🔢 [Smart ID] Generated numbered ID:', numberedId, 'for type:', type);
        }
        return numberedId;
      }
    }
  }
  
  // 最後回退到通用ID
  const fallbackId = `${type.toUpperCase()}1`;
  if (debugMode) {
    devLog('❓ [Smart ID] Using fallback ID:', fallbackId, 'for type:', type);
  }
  return fallbackId;
};

/**
 * 批量分析ID並返回詳細信息
 * @param ids - ID列表
 * @param debugMode - 是否啟用debug模式
 * @returns 包含詳細信息的數組
 */
export const analyzeInstrumentIds = (ids: string[], debugMode: boolean = false): AnalysisResult[] => {
  const results = ids.map(id => {
    const config = getInstrumentConfigById(id, debugMode);
    return {
      id,
      ...config,
      matchType: exactConfigs[id] ? 'exact' : 
                 typePatterns.some(p => p.patterns.test(id)) ? 'pattern' : 'fallback'
    } as AnalysisResult;
  });
  
  if (debugMode) {
    devLog('📊 [Smart ID] Analysis results:', results);
  }
  
  return results;
}; 